"use client";

import { Button } from "@packages/components/ui/Button";
import Card from "@packages/components/ui/Card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@packages/components/ui/Dialog";
import Heading from "@packages/components/ui/Heading";
import React, { useEffect, useState } from "react";

interface TranscriptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transcriptPath: string; // e.g. under "public" "/transcripts/hypertrophy/workout-1.md"
  videoTitle?: string;
}

const TranscriptDialog: React.FC<TranscriptDialogProps> = ({
  open,
  onOpenChange,
  transcriptPath,
  videoTitle,
}) => {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setContent("");
    fetch(transcriptPath)
      .then((res) => {
        if (!res.ok) throw new Error("Transcript not found");
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((_err) => {
        setError("Transcript not available.");
        setLoading(false);
      });
  }, [open, transcriptPath]);

  // Split on double line breaks for paragraphs
  const paragraphs = content
    ? content
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl px-10 py-16 lg:px-12">
        <DialogHeader>
          <DialogTitle className="text-left">
            <Heading
              level={2}
              heading={`Video Transcript`}
              subheading={`${videoTitle}`}
              className="mb-2"
            />
          </DialogTitle>
          <DialogDescription>
            This is an autogenerated transcript for the video. Formatting may
            vary.
          </DialogDescription>
        </DialogHeader>
        <Card
          className="max-h-[60vh] overflow-y-auto"
          as="section"
          variant="sub"
        >
          <>
            {loading && <p>Loading transcript…</p>}
            {error && <p className="text-destructive font-medium">{error}</p>}
            {!loading &&
              !error &&
              paragraphs.map((p, i) => (
                <p key={i} className="mb-4">
                  {p}
                </p>
              ))}
          </>
        </Card>
        <DialogClose asChild>
          <Button variant="outline" size="lg" className="mt-4">
            Close
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};

export { TranscriptDialog };
